import React, { useState, useEffect, useCallback } from "react";
import { Search, Image, Calendar, X } from "lucide-react";
import { imageAnalysisService, ImageAnalysis, PaginationData } from "../../services/imageAnalysisService";
import { format } from "date-fns";

interface ImageAnalysisListProps {
  searchQuery: string;
  onSearchChange: (query: string) => void;
  selectedAnalysisId: string | null;
  onAnalysisSelect: (id: string) => void;
}

export const ImageAnalysisList: React.FC<ImageAnalysisListProps> = ({
  searchQuery,
  onSearchChange,
  selectedAnalysisId,
  onAnalysisSelect,
}) => {
  const [analyses, setAnalyses] = useState<ImageAnalysis[]>([]);
  const [pagination, setPagination] = useState<PaginationData>({
    page: 1,
    per_page: 20,
    total: 0,
    pages: 0,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);

  const fetchAnalyses = useCallback(
    async (page: number, perPage: number) => {
      try {
        setIsLoading(true);
        const response = await imageAnalysisService.getImageAnalyses(page, perPage);
        if (page === 1) {
          setAnalyses(response.analyses);
        } else {
          setAnalyses((prevAnalyses) => [...prevAnalyses, ...response.analyses]);
        }
        setPagination(response.pagination);
        setHasMore(response.pagination.page < response.pagination.pages);
      } catch (error) {
        console.error("Error fetching image analyses:", error);
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    fetchAnalyses(1, pagination.per_page);
  }, [fetchAnalyses, pagination.per_page]);

  const handleLoadMore = () => {
    if (hasMore && !isLoading) {
      fetchAnalyses(pagination.page + 1, pagination.per_page);
    }
  };

  const filteredAnalyses = analyses.filter((analysis) => {
    // If no search query, include all items
    if (!searchQuery) return true;
    
    const query = searchQuery.toLowerCase();
    
    // Check if image_name exists and matches search
    if (analysis.image_name && analysis.image_name.toLowerCase().includes(query)) {
      return true;
    }
    
    // Check if title exists and matches search
    if (analysis.title && analysis.title.toLowerCase().includes(query)) {
      return true;
    }
    
    // Check if analysis object exists and has title that matches search
    if (analysis.analysis && analysis.analysis.title && 
        analysis.analysis.title.toLowerCase().includes(query)) {
      return true;
    }
    
    // Default to false if no matches found
    return false;
  });

  const formatAnalysisDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return format(date, "MMM d, yyyy h:mm a");
    } catch (error) {
      return "Invalid date";
    }
  };

  const getAnalysisTitle = (analysis: any): string => {
    // Check for title in different possible locations based on the data structure
    if (analysis.analysis && analysis.analysis.title) {
      return analysis.analysis.title;
    }
    if (analysis.title) {
      return analysis.title;
    }
    if (analysis.image_name) {
      return analysis.image_name;
    }
    if (analysis.filename) {
      return analysis.filename;
    }
    return "Untitled Analysis";
  };

  const getAnalysisDate = (analysis: any): string => {
    // Check for date in different possible locations
    return analysis.date || analysis.created_at || "";
  };

  const StatusLabel = ({ status }: { status: string }) => {
    let backgroundColor = "bg-gray-100";
    let textColor = "text-gray-500";

    if (status.toLowerCase() === "completed") {
      backgroundColor = "bg-green-100";
      textColor = "text-green-600";
    } else if (status.toLowerCase() === "failed" || status.toLowerCase().includes("failed")) {
      backgroundColor = "bg-red-100";
      textColor = "text-red-600";
    } else if (
      status.toLowerCase() === "processing" ||
      status.toLowerCase() === "analyzing" ||
      status.toLowerCase().includes("processing")
    ) {
      backgroundColor = "bg-blue-100";
      textColor = "text-blue-600";
    }

    return (
      <div
        className={`inline-flex items-center px-2 py-1 rounded-full ${backgroundColor} ${textColor} text-xs font-medium`}
      >
        {status}
      </div>
    );
  };

  return (
    <div className="w-80 border-r border-gray-200 flex flex-col h-full overflow-hidden">
      {/* Search Bar */}
      <div className="p-4 border-b border-gray-200">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-4 w-4 text-gray-400" />
          </div>
          <input
            type="text"
            placeholder="Search images..."
            className="block w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 text-sm"
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
          />
          {searchQuery && (
            <button
              className="absolute inset-y-0 right-0 pr-3 flex items-center"
              onClick={() => onSearchChange("")}
            >
              <X className="h-4 w-4 text-gray-400" />
            </button>
          )}
        </div>
      </div>

      {/* Analysis List */}
      <div className="flex-1 overflow-y-auto">
        {filteredAnalyses.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full p-4 text-center">
            <Image className="h-12 w-12 text-gray-300 mb-2" />
            <p className="text-gray-500 text-sm">
              {isLoading
                ? "Loading image analyses..."
                : searchQuery
                ? "No matching image analyses found."
                : "No image analyses yet."}
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-gray-200">
            {filteredAnalyses.map((analysis) => (
              <li key={analysis._id || `recording-${analysis.recording_id}`}>
                <button
                  className={`w-full text-left px-4 py-3 hover:bg-gray-50 focus:outline-none ${
                    selectedAnalysisId === analysis.recording_id.toString()
                      ? "bg-blue-50"
                      : ""
                  }`}
                  onClick={() => onAnalysisSelect(analysis.recording_id.toString())}
                >
                  <div className="flex items-start">
                    <div className="flex-shrink-0 w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                      <Image className="h-5 w-5 text-gray-500" />
                    </div>
                    <div className="ml-3 flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {getAnalysisTitle(analysis)}
                      </p>
                      <div className="flex items-center text-xs text-gray-500 mt-1">
                        <Calendar className="h-3 w-3 mr-1" />
                        <span>{formatAnalysisDate(getAnalysisDate(analysis))}</span>
                      </div>
                      <div className="mt-2">
                        <StatusLabel status={analysis.status} />
                      </div>
                    </div>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Load More Button */}
      {hasMore && (
        <div className="p-4 border-t border-gray-200">
          <button
            className="w-full py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            onClick={handleLoadMore}
            disabled={isLoading}
          >
            {isLoading ? "Loading..." : "Load More"}
          </button>
        </div>
      )}
    </div>
  );
}; 